import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, FilePlus2, FlaskConical, Upload } from "lucide-react";
import { toast } from "sonner";
import { downloadBlob } from "@/components/ToolLayout";
import { useProject } from "@/lib/workflow/useProject";
import { emptyProject, parseProjectFile, serializeProject } from "@/lib/workflow/projectFile";
import { sampleProject, projectHasData } from "@/lib/workflow/sample";
import { useEmbedded } from "@/components/EmbedContext";
import { useSuiteAccess } from "@/components/EventSuiteAccess";
import { AccountProjects } from "@/components/AccountProjects";
import { LIVE_APPS_READY, PublishEvent } from "@/components/PublishEvent";
import { EmailGuests } from "@/components/EmailGuests";
import { TextReminders } from "@/components/TextReminders";

const fileSafe = (s: string) => s.replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "project";

/**
 * Save, open, start and try the shared project that connected tools use. The project lives in this browser tab
 * only and is erased when the tab closes, so "Save project" is how a person keeps it.
 */
export function ProjectBar({ passKeyNote = true, neutralName, onSaved, onOpened }: { /** Called after the project file is downloaded. */ onSaved?: () => void; /** Called after a project file is opened. */ onOpened?: () => void; /** Shown instead of the default event project name ("My event") on tools that are not about events. */ neutralName?: string; /** Only tools that deal with guest passes need the pass-key warning; a document tool shows it once event pages are in use. */ passKeyNote?: boolean } = {}) {
  const [project, store] = useProject();
  const input = useRef<HTMLInputElement>(null);
  const embedded = useEmbedded();
  const { premium, entitled } = useSuiteAccess();

  const save = () => { downloadBlob(new Blob([serializeProject(project)], { type: "application/json" }), `${fileSafe(project.name)}.fdp-project.json`); onSaved?.(); };
  const open = async (f: File | undefined) => {
    if (!f) return;
    const r = parseProjectFile(await f.text());
    if (!r.ok) { toast.error(r.error); return; }
    store.reset(r.project);
    onOpened?.();
    const bad = r.issues.filter((i) => i.level === "error").length;
    toast.success(bad ? `Project opened. ${bad} entr${bad === 1 ? "y" : "ies"} could not be read.` : "Project opened");
  };
  // An in-page confirmation, not window.confirm: a native dialog blocks the whole tab (and automated browsers never answer it).
  const [ask, setAsk] = useState<"sample" | "fresh" | null>(null);
  const doFresh = () => { setAsk(null); store.reset(emptyProject({ currency: project.currency, locale: project.locale, timeZone: project.timeZone, paper: project.paper })); };
  const doSample = () => { setAsk(null); store.reset(sampleProject()); };
  const fresh = () => { if (projectHasData(project)) setAsk("fresh"); else doFresh(); };
  const sample = () => { if (projectHasData(project)) setAsk("sample"); else doSample(); };

  if (embedded) return null;
  return (
    <>
    <div className="flex flex-wrap items-center gap-2 mt-4 text-xs text-muted-foreground" data-testid="project-bar">
      <span>Project: <strong className="text-foreground">{neutralName && project.name === "My event" ? neutralName : project.name}</strong>. {entitled ? "Kept in this tab until you save it to your account." : "Kept in this tab only, then erased."}</span>
      <Button variant="outline" size="sm" onClick={save} aria-describedby={passKeyNote ? "project-file-note" : undefined}><Download className="h-3.5 w-3.5 mr-1" />Save project</Button>
      <Button variant="outline" size="sm" onClick={() => input.current?.click()}><Upload className="h-3.5 w-3.5 mr-1" />Open project</Button>
      <Button variant="ghost" size="sm" onClick={sample}><FlaskConical className="h-3.5 w-3.5 mr-1" />Try a sample</Button>
      <Button variant="ghost" size="sm" onClick={fresh}><FilePlus2 className="h-3.5 w-3.5 mr-1" />New project</Button>
      {passKeyNote && (
        <span id="project-file-note" className="w-full" data-testid="project-file-note">
          The saved file includes a pass key: the secret that signs the QR codes on guest passes. It does not unlock any plan or paid feature, but anyone holding the file can make passes that check in, so share it only with people you trust with your door.
        </span>
      )}
      {ask && (
        <span className="flex flex-wrap items-center gap-2 w-full rounded border bg-muted/40 px-2 py-1.5 text-foreground" role="alertdialog" aria-label="Replace the open project?" data-testid="project-confirm">
          {ask === "sample" ? "Load the sample project? The open one is replaced unless you saved it." : "Start a new empty project? The open one is cleared unless you saved it."}
          <Button size="sm" onClick={ask === "sample" ? doSample : doFresh} data-testid="project-confirm-yes">{ask === "sample" ? "Load sample" : "Start new"}</Button>
          <Button size="sm" variant="ghost" onClick={() => setAsk(null)}>Cancel</Button>
        </span>
      )}
      <input ref={input} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { void open(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
    {entitled && <AccountProjects />}
    {premium && LIVE_APPS_READY && <PublishEvent />}
    {premium && <EmailGuests />}
    {premium && <TextReminders />}
    </>
  );
}
